import React, { useEffect, useState } from "react";
import {
  getAllCompanySubCategories,
  createCompanySubCategory,
  updateCompanySubCategory,
  deleteCompanySubCategory,
  getAllCompanyCategories
} from "../../../services/adminService";
import {
  Button,
  Table,
  Form,
  Modal,
  Spinner,
  Alert,
} from "react-bootstrap";

const CompanySubCategory = () => {
  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editSubCategory, setEditSubCategory] = useState(null);
  const [formData, setFormData] = useState({ name: "", parentCategory: "", description: "" });

  // Fetch SubCategories + Categories
  const fetchData = async () => {
    try {
      setLoading(true);
      const [subRes, catRes] = await Promise.all([
        getAllCompanySubCategories(),
        getAllCompanyCategories()
      ]);
      setSubCategories(subRes.data || []);
      setCategories(catRes.data || []);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch subcategories");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Save (Create/Update)
  const handleSave = async () => {
    try {
      if (editSubCategory) {
        await updateCompanySubCategory(editSubCategory._id, formData);
      } else {
        await createCompanySubCategory(formData);
      }
      setShowModal(false);
      setFormData({ name: "", parentCategory: "", description: "" });
      setEditSubCategory(null);
      fetchData();
    } catch (err) {
      setError("Failed to save subcategory");
    }
  };

  // Delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this subcategory?")) {
      try {
        await deleteCompanySubCategory(id);
        setSubCategories((prev) => prev.filter((s) => s._id !== id));
      } catch (err) {
        setError("Failed to delete subcategory");
      }
    }
  };

  // Edit
  const handleEdit = (sub) => {
    setEditSubCategory(sub);
    setFormData({
      name: sub.name,
      parentCategory: sub.parentCategory?._id || "",
      description: sub.description || "",
    });
    setShowModal(true);
  };

  return (
    <div className="container mt-4">
      <h2 className="mb-3 text-center fw-bold">📂 Company SubCategories</h2>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="d-flex justify-content-end mb-3">
        <Button variant="primary" onClick={() => setShowModal(true)}>
          + Add New SubCategory
        </Button>
      </div>

      {loading ? (
        <div className="text-center"><Spinner animation="border" /></div>
      ) : (
        <Table striped bordered hover responsive className="align-middle text-center shadow-sm">
          <thead className="table-dark">
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Parent Category</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {subCategories.length > 0 ? (
              subCategories.map((sub, index) => (
                <tr key={sub._id}>
                  <td>{index + 1}</td>
                  <td className="fw-semibold">{sub.name}</td>
                  <td>{sub.parentCategory?.name || "—"}</td>
                  <td>{sub.description || "—"}</td>
                  <td>
                    <Button variant="warning" size="sm" className="me-2" onClick={() => handleEdit(sub)}>
                      ✏️ Edit
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(sub._id)}>
                      🗑 Delete
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="5" className="text-center text-muted">No subcategories found</td></tr>
            )}
          </tbody>
        </Table>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editSubCategory ? "Edit SubCategory" : "Add SubCategory"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="subCategoryName" className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Enter subcategory name" />
            </Form.Group>

            <Form.Group controlId="parentCategory" className="mb-3">
              <Form.Label>Parent Category</Form.Label>
              <Form.Select name="parentCategory" value={formData.parentCategory} onChange={handleChange}>
                <option value="">Select Parent Category</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>{cat.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group controlId="description" className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} name="description" value={formData.description} onChange={handleChange} placeholder="Enter description" />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>{editSubCategory ? "Update" : "Save"}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CompanySubCategory;
